export interface TenantThemeColors {
  primaryColor: string | null;
  secondaryColor: string | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  metadata?: {
    title?: string | null;
    description?: string | null;
  } | null;
  name?: string | null;
  title?: string | null;
  description?: string | null;
  paymentMethod?: 'prepaid' | 'postpaid' | null;
  currency?: string | null;
}

export interface FullTheme {
  primary: string;
  secondary: string;
  accent: string;
  primaryScale: Record<string, string>;
  secondaryScale: Record<string, string>;
  primaryForeground: string;
}

const FALLBACK_PRIMARY = '#117CFF';
const FALLBACK_SECONDARY = '#1E1E1E';
const FALLBACK_ACCENT = '#FFD400';

const isHexColor = (value: string | null | undefined): value is string =>
  !!value && /^#([A-Fa-f0-9]{6})$/.test(value);

const normalizeColor = (value: string | null | undefined, fallback: string) =>
  isHexColor(value) ? value : fallback;

export function hexToHSL(hex: string) {
  let r = 0,
    g = 0,
    b = 0;

  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16) / 255;
    g = parseInt(hex.slice(3, 5), 16) / 255;
    b = parseInt(hex.slice(5, 7), 16) / 255;
  }

  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export const generateScale = (hex: string) => {
  const { h, s } = hexToHSL(hex);

  const steps = [95, 90, 80, 70, 60, 50, 40, 30, 20];

  const scale: Record<string, string> = {};

  steps.forEach((lightness, index) => {
    const key = (index + 1) * 100;
    scale[key] = `hsl(${h}, ${s}%, ${lightness}%)`;
  });

  return scale;
};

export const getReadableForeground = (hex: string) => {
  const { l } = hexToHSL(hex);
  return l > 60 ? '#000000' : '#ffffff';
};

export const buildTheme = (base: TenantThemeColors): FullTheme => {
  const primary = normalizeColor(base.primaryColor, FALLBACK_PRIMARY);
  const secondary = normalizeColor(base.secondaryColor, FALLBACK_SECONDARY);

  const primaryScale = generateScale(primary);
  const secondaryScale = generateScale(secondary);

  return {
    primary,
    secondary,
    accent: primaryScale[500] ?? FALLBACK_ACCENT,
    primaryScale,
    secondaryScale,
    primaryForeground: getReadableForeground(primary),
  };
};

export const applyTheme = (theme: FullTheme) => {
  const root = document.documentElement;

  root.style.setProperty('--color-primary', theme.primary);
  root.style.setProperty('--color-secondary', theme.secondary);
  root.style.setProperty('--color-accent', theme.accent);
  root.style.setProperty('--color-primary-foreground', theme.primaryForeground);

  Object.entries(theme.primaryScale).forEach(([step, value]) => {
    root.style.setProperty(`--color-primary-${step}`, value);
  });

  Object.entries(theme.secondaryScale).forEach(([step, value]) => {
    root.style.setProperty(`--color-secondary-${step}`, value);
  });
};

export const resolveTheme = (
  base: TenantThemeColors | null | undefined
): FullTheme =>
  buildTheme({
    primaryColor: base?.primaryColor ?? FALLBACK_PRIMARY,
    secondaryColor: base?.secondaryColor ?? FALLBACK_SECONDARY,
  });

// const switchPrimary = (newPrimary: string) => {
//   const base = {
//     primaryColor: newPrimary,
//     secondaryColor: currentTheme.secondary,
//   };

//   const full = buildTheme(base);

//   applyTheme(full);
//   setThemeCookie(full);
// };
